import { useState, useEffect } from 'react'
import { Modal } from '../ui/Modal'
import type { Camera } from '../../types'

interface Props {
  open: boolean
  camera: Camera | null
  onClose: () => void
  onSave: (id: string, updates: Partial<Pick<Camera, 'name' | 'cam_ip' | 'port' | 'external_ip' | 'rtsp_path' | 'username' | 'password'>>) => Promise<void>
}

export function EditCameraDialog({ open, camera, onClose, onSave }: Props) {
  const [name, setName] = useState('')
  const [camIp, setCamIp] = useState('')
  const [port, setPort] = useState('80')
  const [externalIp, setExternalIp] = useState('')
  const [rtspPath, setRtspPath] = useState('')
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Reset form when camera changes
  useEffect(() => {
    if (camera) {
      setName(camera.name)
      setCamIp(camera.cam_ip)
      setPort(camera.port.toString())
      setExternalIp(camera.external_ip ?? '')
      setRtspPath(camera.rtsp_path)
      setUsername(camera.username)
      setPassword(camera.password)
      setError(null)
    }
  }, [camera])

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!camera) return

    setSaving(true)
    setError(null)
    try {
      await onSave(camera.id, {
        name: name.trim() || camera.name,
        cam_ip: camIp.trim(),
        port: parseInt(port) || 443,
        external_ip: externalIp.trim() || null,
        rtsp_path: rtspPath.trim(),
        username: username.trim(),
        password: password.trim(),
      })
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save')
    } finally {
      setSaving(false)
    }
  }

  const inputCls = 'w-full bg-gray-50 border border-gray-200 rounded-md px-3 py-2 text-gray-900 text-sm placeholder-gray-400 focus:outline-none focus:border-brand focus:ring-1 focus:ring-brand/20'
  const labelCls = 'block text-xs uppercase tracking-wide text-gray-500 mb-1.5'

  return (
    <Modal open={open} onClose={onClose} title={`Edit — ${camera?.name ?? 'Camera'}`}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className={labelCls}>Name</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className={inputCls}
            autoFocus
          />
        </div>

        <div>
          <label className={labelCls}>External IP</label>
          <input
            type="text"
            value={externalIp}
            onChange={(e) => setExternalIp(e.target.value)}
            placeholder="e.g. 81.133.159.99"
            className={inputCls}
          />
          <p className="text-[10px] text-gray-500 mt-1">Public IP where the camera can be reached (auto-creates DNS proxy)</p>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <div className="col-span-2">
            <label className={labelCls}>Camera LAN IP</label>
            <input
              type="text"
              value={camIp}
              onChange={(e) => setCamIp(e.target.value)}
              placeholder="192.168.0.121"
              className={inputCls}
            />
          </div>
          <div>
            <label className={labelCls}>External Port</label>
            <input
              type="number"
              value={port}
              onChange={(e) => setPort(e.target.value)}
              className={inputCls}
            />
          </div>
        </div>

        <div>
          <label className={labelCls}>RTSP Path</label>
          <input
            type="text"
            value={rtspPath}
            onChange={(e) => setRtspPath(e.target.value)}
            className={inputCls}
          />
          <p className="text-[10px] text-gray-500 mt-1">
            /snl/live/1/2 = sub stream (SD), /snl/live/1/1 = main stream (HD)
          </p>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelCls}>Username</label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className={inputCls}
            />
          </div>
          <div>
            <label className={labelCls}>Password</label>
            <input
              type="text"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={inputCls}
            />
          </div>
        </div>

        {camera && (
          <div className="bg-gray-50 border border-gray-200 rounded-md px-3 py-2 space-y-1">
            <p className="text-[10px] text-gray-500">
              Relay Key: <span className="font-mono text-gray-700">{camera.stream_name}</span>
            </p>
            <p className="text-[10px] text-gray-500">
              Cam #: <span className="text-gray-700">{camera.cam_number}</span>
            </p>
            <p className="text-[10px] text-gray-500">
              RTSP: <span className="font-mono text-gray-700">rtsp://{username}:{password}@{camIp}:554{rtspPath}</span>
            </p>
          </div>
        )}

        {error && (
          <p className="text-error text-sm">{error}</p>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-500 hover:text-gray-900 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="bg-brand hover:bg-brand-dark text-white text-sm font-medium px-4 py-2 rounded-md transition-colors disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
    </Modal>
  )
}
